/* eslint-disable max-lines */
import { useHistory } from 'react-router';
import AppRouter from 'common/AppRouter';
import { useSelector, useDispatch } from 'react-redux';
import TextWithTooltipOnEllipsis from 'components/base/TextWithTooltipOnEllipsis';
import { PageLayout } from 'components/base/layout-components';
import TeamAvatar from 'components/base/TeamAvatar';
import InactiveTooltipTitle from '../InactiveTooltipTitle';
import {
  uiStateSelector,
  hasNoDataSelector,
  selectedTeamSelector
} from 'state/selectors/ui-state';
import {
  isTeamLicenseSelector,
  licenseSelector
} from 'state/selectors/license';
import {
  isSelectedTeamParentSelector,
  getRootTeamSelector,
  teamsSelector
} from 'state/selectors/teams';
import {
  myAccountSelector,
  isAdminSelector,
  isSuperUserSelector
} from 'state/selectors/account';
import { getAllTeamsGroup } from 'common/utils/utils';
import useSettingsService from 'components/hooks/useSettingsService';
import useTabsBuilder from 'components/RouterTabs/useTabsBuilder';
import {
  lightColorPalette,
  HomeIcon,
  ProjectsIcon,
  AnalyticsIcon,
  AllocationIcon,
  AutomationIcon,
  SettingsIcon,
  HelpContainedIcon,
  DeliveryIcon,
  PulseIcon,
  ProgressionIcon,
  MetricsIcon,
  GitActivityIcon,
  InvestmentStrategyIcon,
  CoachingIcon,
  AutomationsDashboardIcon,
  MarketplaceIcon,
  TeamIcon,
  UsersIcon,
  DonutIcon,
  GoalsIcon,
  SidebarMenuNewBadge,
  SidebarMenuProBadge,
  SidebarMenuSoonBadge,
  SidebarMenuTeamViewBadge,
  SidebarMenuEarlyAccessBetaBadge,
  ProductivityGainIcon
} from '@linearb/linui-common';
import {
  ACCOUNT_TYPES_MAP,
  FEATURE_FLAGS_NAMES,
  CAN_EDIT_ACCOUNT_TYPES,
  PERMISSION_DOMAIN,
  PERMISSION_ACTION
} from '@linearb/linjs-common';
import {
  GIT_PROVIDERS,
  PM_PROVIDERS,
  SETTING_NAMES,
  TEAM_TYPES
} from 'common/constants/constants';
import Dashboard from 'views/root/views/home/views/dashboard';
import Activity from 'views/root/views/home/views/activity';
import Contributors from 'views/root/views/home/views/people';
import TeamsCTA from 'components/CtaPage/teamsCta/TeamsCTA';
import Performance from 'views/root/views/home/views/performance/Performance';
import TeamGoals from 'views/root/views/home/views/performance/TeamGoals';
import Pulse from 'views/root/views/home/views/pulse';
import ResourceAllocationOverview from 'views/root/views/home/views/projects/ResourceAllocationOverview';
import ResourceAllocationConfig from 'views/root/views/home/views/projects/ResourceAllocationOverview/settings/SettingsModal';
import ResourceAllocationCta from 'views/root/views/home/views/projects/ResourceAllocationOverview/ResourceAllocationCTA';
import InvestmentCarvingCta from 'views/root/views/home/views/projects/InvestmentCarving/InvestmentCarvingCTA';
import InvestmentCarving from 'views/root/views/home/views/projects/InvestmentCarving';
import InvestmentCarvingConfig from 'views/root/views/home/views/projects/InvestmentCarving/settings/Configuration/Configuration';
import ProjectsCTA from 'views/root/views/home/views/projects/ProjectsApi/ProjectsCTA';
import ProjectsApi from 'views/root/views/home/views/projects/ProjectsApi';
import DevCoaching from 'views/root/views/home/views/devCoaching';
import TeamGoalsCTA from 'components/CtaPage/teamGoalsCta/TeamGoalsCTA';
import { EMPTY_DUMMY_ORG_LEVEL_TEAM_GOALS_DASHBOARD } from 'views/root/views/home/views/performance/views/CustomReport/consts';
import { selectTeamId } from 'state/actions';
import { handleTeamChange } from 'connected-components/TopBar/TopBar.service';
import ForecastingRouter from 'views/root/views/home/views/projects/Forecasting/ForecastingRouter';
import usePermissionDomainGate from 'common/hooks/usePermissionDomainGate';
import { Box } from '@mui/material';
import { Productivity } from 'views/root/views/home/views/productivity/Productivity';
import { isPmToolAzureBoardsSelector } from 'state/selectors/sensors-authorizations';
import { LBSurveysIcon } from '@linearb/buzz';
import { Surveys } from 'views/root/views/home/views/surveys/Surveys';

const badgeBlueBG = 'hsl(var(--secondary-hover))';

const BetaBadge = () => (
  <SidebarMenuEarlyAccessBetaBadge color={badgeBlueBG} text='Beta' />
);

interface UseSidebarButtonsProps {
  isLoadingIterations?: boolean;
  isDefaultIterationTooLong?: boolean;
  disableTeamPicker?: boolean;
  setDisableTeamPicker?: (value: boolean) => void;
  gitScopeToggle?: unknown;
}

// eslint-disable-next-line max-lines-per-function
const useSidebarButtons = ({
  isLoadingIterations,
  isDefaultIterationTooLong,
  disableTeamPicker,
  setDisableTeamPicker,
  gitScopeToggle
}: UseSidebarButtonsProps = {}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { getSettingOrDefinitionValue } = useSettingsService();
  const license = useSelector(licenseSelector);
  const account = useSelector(myAccountSelector);
  const isAzureBoardsProvider = useSelector(isPmToolAzureBoardsSelector);

  const { isPaid, isTrial } = license;
  const isFreePlan = !isPaid && !isTrial;

  const isRBACEnabledFF = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.isRBACEnabled
  });

  const isDevCoachingEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.isDevCoachingEnabled
  });

  const isCanConfigProjectsFeatureFlagOn = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.canConfigProjects
  });
  const showPersonalInformation = getSettingOrDefinitionValue({
    name: SETTING_NAMES.SHOW_PERSONAL_INFORMATION
  });
  const isAzureBoardsPdtEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.isAzureBoardsPdtEnabled
  });
  const isAzureBoardsPulseEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.isAzureBoardsPulseEnabled
  });
  const isTeamSpaceEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.isTeamSpaceEnabled
  });
  const isProductivityGainEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.isProductivityGainEnabled
  });

  const { isPermitted } = usePermissionDomainGate();
  const devCoachingEnabledForUser = isPermitted(
    PERMISSION_DOMAIN.DEV_COACHING,
    PERMISSION_ACTION.VIEW
  );

  const teams = useSelector(teamsSelector);
  const isAdmin = useSelector(isAdminSelector);
  const isSuperUser = useSelector(isSuperUserSelector);
  const navigationButtonsBuilder = useTabsBuilder();
  const buttonsBuilder = useTabsBuilder();
  const selectedTeam = useSelector(selectedTeamSelector);
  const isGroup = useSelector(isSelectedTeamParentSelector);
  const isTeamLicense = useSelector(isTeamLicenseSelector);
  const hasNoData = useSelector(hasNoDataSelector);
  const uiState = useSelector(uiStateSelector);
  const rootTeamId = useSelector(getRootTeamSelector);
  const canAccessAllCompanySettings = isRBACEnabledFF
    ? isAdmin || isSuperUser
    : true;
  const {
    id: selectedTeamId,
    type: selectedTeamType,
    name: selectedTeamName
  } = selectedTeam || {};

  const isAllTeams =
    selectedTeamType === TEAM_TYPES.ALL_TEAMS || !selectedTeamId;

  const isAllContributors = selectedTeamType === TEAM_TYPES.ALL_CONTRIBUTORS;

  const isCanConfigProjects =
    CAN_EDIT_ACCOUNT_TYPES.includes(account?.type_id) &&
    isCanConfigProjectsFeatureFlagOn;

  const {
    dates: { from, to }
  } = uiState;

  const buttonIconSize = 24;
  const menuIconSize = 18;
  const topBarIconSize = 18;
  const iconColor = lightColorPalette.lbGrey_50;
  const topBarIconColor = lightColorPalette.lbGrey_50;
  const canSeeDevCoaching = isAdmin || isSuperUser || devCoachingEnabledForUser;

  const pulseMenuItem = {
    id: 'pulse',
    title: isTeamSpaceEnabled ? 'Iterations' : 'Pulse',
    subtitle:
      'Get a team-based view of project execution trends for current and past iterations',
    icon: <PulseIcon size={menuIconSize} color={iconColor} />,
    topBarIcon: <PulseIcon size={topBarIconSize} color={topBarIconColor} />,
    tooltipTitle: hasNoData && <InactiveTooltipTitle />,
    badge: isTeamLicense ? (
      <SidebarMenuProBadge color={badgeBlueBG} />
    ) : isAzureBoardsProvider ? (
      <BetaBadge />
    ) : isTeamSpaceEnabled ? null : (
      <SidebarMenuTeamViewBadge color={badgeBlueBG} />
    ),
    showBadgeInTopBar: true,
    permissions: {
      ...(isTeamLicense
        ? {}
        : {
            featureFlagName: !FEATURE_FLAGS_NAMES.hidePulseEnabled,
            pmProviders: [
              PM_PROVIDERS.JIRA,
              PM_PROVIDERS.CLUBHOUSE,
              isAzureBoardsPulseEnabled &&
                isAzureBoardsPdtEnabled &&
                PM_PROVIDERS.AZURE_BOARDS
            ].filter(Boolean)
          })
    },
    ...(!hasNoData && {
      path: AppRouter.PULSE.path,
      component: <Pulse />
    })
  };

  const activityMenuItem = {
    id: 'team_activity',
    title: isTeamSpaceEnabled ? 'Activity' : 'Team Activity',
    subtitle: 'Track WIP, avoid burnout, and maintain team health',
    icon: <TeamIcon size={menuIconSize} color={iconColor} />,
    topBarIcon: <TeamIcon size={topBarIconSize} color={topBarIconColor} />,
    tooltipTitle: hasNoData && <InactiveTooltipTitle />,
    isExact: false,
    isSelected:
      `/${history.location.pathname.split('/')[1]}` ===
        AppRouter.CONTRIBUTORS.path ||
      history.location.pathname === AppRouter.TEAMS_CTA.path,
    ...(!hasNoData && {
      path: isFreePlan ? AppRouter.TEAMS_CTA.path : AppRouter.CONTRIBUTORS.path,
      ...(isFreePlan
        ? {}
        : {
            search: `time=${from},${to}`
          }),

      component: isFreePlan ? (
        <PageLayout>
          <TeamsCTA />
        </PageLayout>
      ) : (
        <Contributors gitScopeToggle={gitScopeToggle} />
      )
    })
  };

  navigationButtonsBuilder.initTabs([
    {
      // @ts-ignore
      id: 'home',
      title: 'Home',
      isBorderTop: true,
      icon: <HomeIcon size={buttonIconSize} />,
      topBarIcon: <HomeIcon size={topBarIconSize} color={topBarIconColor} />,
      path: AppRouter.PAGE_DASHBOARD.path,
      component: (
        <Dashboard
          isLoadingIterations={isLoadingIterations}
          isDefaultIterationTooLong={isDefaultIterationTooLong}
          gitScopeToggle={gitScopeToggle}
        />
      ),
      isSelected:
        history.location.pathname === AppRouter.ROOT.path ||
        history.location.pathname === AppRouter.PAGE_DASHBOARD.path
    },

    {
      id: 'productivity_gain',
      title: 'Productivity',
      isBorderTop: true,
      icon: <ProductivityGainIcon size={buttonIconSize} />,
      topBarIcon: (
        <ProductivityGainIcon size={topBarIconSize} color={topBarIconColor} />
      ),
      permissions: {
        featureFlagName: isProductivityGainEnabled
          ? FEATURE_FLAGS_NAMES.isProductivityGainEnabled
          : FEATURE_FLAGS_NAMES.isSurveysEnabled
      },
      badge: <BetaBadge />,
      showBadgeInTopBar: FEATURE_FLAGS_NAMES.isProductivityGainEnabled,
      menu: [
        {
          type: 'subtitle',
          title:
            'Unlock efficiency with team feedback and actionable automation insights'
        },
        {
          id: 'productivity',
          title: 'Productivity',
          subtitle:
            'See gains, explore use cases, and install automations to boost output',
          icon: <ProductivityGainIcon size={menuIconSize} color={iconColor} />,
          topBarIcon: (
            <ProductivityGainIcon
              size={topBarIconSize}
              color={topBarIconColor}
            />
          ),
          badge: <SidebarMenuNewBadge color={badgeBlueBG} />,
          permissions: {
            featureFlagName: FEATURE_FLAGS_NAMES.isProductivityGainEnabled
          },
          isExact: false,
          path: AppRouter.PRODUCTIVITY_GAIN.path,
          component: <Productivity />
        }
      ]
    },
    {
      id: 'projects',
      title: 'Projects',
      isBorderTop: true,
      icon: <ProjectsIcon size={buttonIconSize} />,
      permissions: {
        ...(isTeamLicense
          ? {}
          : {
              pmProviders: [
                PM_PROVIDERS.JIRA,
                PM_PROVIDERS.CLUBHOUSE,
                PM_PROVIDERS.AZURE_BOARDS
              ]
            })
      },
      menu: [
        {
          type: 'subtitle',
          title:
            'Get a holistic view of project execution and scope work more effectively'
        },
        {
          id: 'forecasting',
          title: 'Forecasting',
          subtitle:
            'Accurately forecast delivery and action insights with project and git data',
          icon: <ProgressionIcon size={menuIconSize} color={iconColor} />,
          topBarIcon: (
            <ProgressionIcon size={topBarIconSize} color={topBarIconColor} />
          ),
          badge: isTeamLicense ? (
            <SidebarMenuProBadge color={badgeBlueBG} />
          ) : (
            <SidebarMenuNewBadge color={badgeBlueBG} />
          ),
          permissions: {
            ...(isTeamLicense
              ? {}
              : {
                  pmProviders: [PM_PROVIDERS.JIRA, PM_PROVIDERS.AZURE_BOARDS],
                  accountTypes: [
                    ACCOUNT_TYPES_MAP.Primary,
                    ACCOUNT_TYPES_MAP.SuperUser,
                    ACCOUNT_TYPES_MAP.Editor,
                    ACCOUNT_TYPES_MAP.Viewer
                  ]
                })
          },
          isExact: false,
          path: AppRouter.PROJECTS_FORECASTING_ROOT.path,
          component: <ForecastingRouter />
        },
        {
          id: 'delivery',
          title: 'Delivery',
          subtitle:
            'See how well teams plan, execute, and adapt to fluctuations in work',
          icon: <DeliveryIcon size={menuIconSize} color={iconColor} />,
          topBarIcon: (
            <DeliveryIcon size={topBarIconSize} color={topBarIconColor} />
          ),
          path: AppRouter.PROJECTS_API.path,
          isExact: false,
          badge: isTeamLicense ? (
            <SidebarMenuProBadge color={badgeBlueBG} />
          ) : (
            isAzureBoardsProvider && <BetaBadge />
          ),
          showBadgeInTopBar: true,
          permissions: {
            ...(isTeamLicense
              ? {}
              : {
                  pmProviders: [
                    PM_PROVIDERS.JIRA,
                    PM_PROVIDERS.CLUBHOUSE,
                    isAzureBoardsPdtEnabled && PM_PROVIDERS.AZURE_BOARDS
                  ].filter(Boolean)
                })
          },
          component: isTeamLicense ? (
            <PageLayout>
              <ProjectsCTA />
            </PageLayout>
          ) : (
            <PageLayout>
              <ProjectsApi isCanConfigProjects={isCanConfigProjects} />
            </PageLayout>
          )
        },
        ...(!isTeamSpaceEnabled ? [pulseMenuItem] : [])
      ]
    },
    {
      id: 'metrics',
      title: 'Metrics',
      isBorderTop: true,
      icon: <AnalyticsIcon size={buttonIconSize} />,
      menu: [
        {
          type: 'subtitle',
          title:
            'Dashboards and deep-dive reporting for all of your engineering data'
        },
        {
          id: 'metrics_dashboard',
          title: 'Metrics Dashboard',
          subtitle:
            'See your DORA, Delivery and Quality metrics with industry benchmarks',
          icon: <MetricsIcon size={menuIconSize} color={iconColor} />,
          topBarIcon: (
            <MetricsIcon size={topBarIconSize} color={topBarIconColor} />
          ),
          tooltipTitle: hasNoData && <InactiveTooltipTitle />,
          isExact: false,
          ...(!hasNoData && {
            path: AppRouter.PERFORMANCE.path,
            component: (
              <Performance
                disableTeamPicker={disableTeamPicker}
                setDisableTeamPicker={setDisableTeamPicker}
              />
            )
          })
        },
        {
          id: 'git_activity',
          title: 'Git Activity',
          subtitle:
            'Pinpoint issues with granular visibility of repos, branches, and PRs',
          icon: <GitActivityIcon size={menuIconSize} color={iconColor} />,
          topBarIcon: (
            <GitActivityIcon size={topBarIconSize} color={topBarIconColor} />
          ),
          tooltipTitle: hasNoData && <InactiveTooltipTitle />,
          isExact: false,
          ...(!hasNoData && {
            path: AppRouter.ACTIVITY.path,
            component: <Activity />
          })
        }
      ]
    },
    {
      id: 'resources',
      title: 'Resources',
      isBorderTop: true,
      icon: <AllocationIcon size={buttonIconSize} />,
      // check if needs Projects permissions and hasNoData
      menu: [
        {
          type: 'subtitle',
          title:
            'Align R&D to business needs and refine project investment strategy'
        },
        {
          id: 'allocation',
          title: 'Allocation',
          topBarTitle: 'Resource Allocation',
          subtitle:
            'Understand and optimize engineering spend with visibility into cost and FTE effort by project, epic, initiative, and more',
          icon: <DonutIcon size={menuIconSize} color={iconColor} />,
          topBarIcon: (
            <DonutIcon size={topBarIconSize} color={topBarIconColor} />
          ),
          path: AppRouter.PROJECTS_RESOURCE_ALLOCATION_OVERVIEW.path,
          badge: isTeamLicense && <SidebarMenuProBadge color={badgeBlueBG} />,
          permissions: {
            ...(isTeamLicense
              ? {}
              : {
                  pmProviders: [PM_PROVIDERS.JIRA, PM_PROVIDERS.AZURE_BOARDS],
                  accountTypes: [
                    ACCOUNT_TYPES_MAP.Primary,
                    ACCOUNT_TYPES_MAP.SuperUser,
                    ACCOUNT_TYPES_MAP.Editor
                  ]
                })
          },
          component: isTeamLicense ? (
            <PageLayout>
              <ResourceAllocationCta />
            </PageLayout>
          ) : (
            <PageLayout>
              <ResourceAllocationOverview />
            </PageLayout>
          ),
          subRoutes: [
            {
              path: AppRouter.PROJECTS_RESOURCE_ALLOCATION_CONFIG.path,
              component: (
                <PageLayout>
                  <ResourceAllocationConfig />
                </PageLayout>
              )
            }
          ]
        },
        {
          id: 'investment_strategy',
          title: 'Investment Strategy',
          subtitle:
            'Visualize and improve how teams spend their dev cycles with investment area data and industry benchmarks',
          icon: (
            <InvestmentStrategyIcon size={menuIconSize} color={iconColor} />
          ),
          topBarIcon: (
            <InvestmentStrategyIcon
              size={topBarIconSize}
              color={topBarIconColor}
            />
          ),
          badge: isTeamLicense && <SidebarMenuProBadge color={badgeBlueBG} />,
          permissions: {
            ...(isTeamLicense
              ? {}
              : {
                  pmProviders: [PM_PROVIDERS.JIRA, PM_PROVIDERS.AZURE_BOARDS],
                  accountTypes: [
                    ACCOUNT_TYPES_MAP.Primary,
                    ACCOUNT_TYPES_MAP.SuperUser,
                    ACCOUNT_TYPES_MAP.Editor,
                    ACCOUNT_TYPES_MAP.Viewer
                  ]
                })
          },
          path: AppRouter.PROJECTS_INVESTMENT_CARVING.path,
          component: isTeamLicense ? (
            <PageLayout>
              <InvestmentCarvingCta />
            </PageLayout>
          ) : (
            <PageLayout>
              <InvestmentCarving />
            </PageLayout>
          ),
          subRoutes: [
            {
              path: AppRouter.PROJECTS_INVESTMENT_CARVING_CONFIG.path,
              component: (
                <PageLayout>
                  <InvestmentCarvingConfig />
                </PageLayout>
              )
            }
          ]
        }
      ]
    },
    {
      id: 'people',
      title: isTeamSpaceEnabled ? 'Teams' : 'People',
      isBorderTop: true,
      icon: <UsersIcon size={buttonIconSize} />,
      menu: [
        {
          type: 'subtitle',
          title: isTeamSpaceEnabled
            ? 'Build a high-performing team culture using insights from your goals, iterations, performance and metrics'
            : 'Learn more about your developers, teams and improve the developer experience'
        },
        ...(isTeamSpaceEnabled ? [pulseMenuItem, activityMenuItem] : []),
        {
          title: 'Goals',
          subtitle:
            'Set data-backed team improvement goals and track progress against them',
          icon: <GoalsIcon size={menuIconSize} color={iconColor} />,
          topBarIcon: (
            <GoalsIcon size={topBarIconSize} color={topBarIconColor} />
          ),
          badge: isTeamLicense ? (
            <SidebarMenuProBadge color={badgeBlueBG} />
          ) : isTeamSpaceEnabled ? null : (
            <SidebarMenuTeamViewBadge color={badgeBlueBG} />
          ),
          isExact: false,
          tooltipTitle: hasNoData && <InactiveTooltipTitle />,
          isSelected: `/${history.location.pathname.split('/')[1]}`.includes(
            AppRouter.TEAM_GOALS.path
          ),
          ...(!hasNoData && {
            ...(isTeamLicense
              ? {}
              : {
                  params:
                    isAllTeams || isAllContributors
                      ? EMPTY_DUMMY_ORG_LEVEL_TEAM_GOALS_DASHBOARD.id
                      : selectedTeamId
                }),
            path: isTeamLicense
              ? AppRouter.PERFORMANCE_TEAM_GOALS_CTA.path
              : AppRouter.TEAM_GOALS.path,
            // if <Route path ... > is differant from item click path - pass it as routePath
            routePath: isTeamLicense
              ? AppRouter.PERFORMANCE_TEAM_GOALS_CTA.path
              : AppRouter.TEAM_GOALS_DASHBOARD.path,
            component: isTeamLicense ? (
              <PageLayout>
                <TeamGoalsCTA showClose={false} />
              </PageLayout>
            ) : (
              <PageLayout>
                <TeamGoals gitScopeToggle={gitScopeToggle} />
              </PageLayout>
            )
          })
        },

        {
          title: 'Coaching',
          subtitle:
            'Help your team members grow with data about their strengths and gaps',
          icon: <CoachingIcon size={menuIconSize} color={iconColor} />,
          topBarIcon: (
            <CoachingIcon size={topBarIconSize} color={topBarIconColor} />
          ),
          ...{
            badge: !isDevCoachingEnabled ? (
              <SidebarMenuSoonBadge color={badgeBlueBG} />
            ) : !isTeamSpaceEnabled &&
              (isAllContributors || !selectedTeamId) ? (
              <SidebarMenuTeamViewBadge color={badgeBlueBG} />
            ) : (
              <BetaBadge />
            )
          },
          showBadgeInTopBar: isDevCoachingEnabled,
          permissions: {
            condition: showPersonalInformation && canSeeDevCoaching
          },
          ...(isDevCoachingEnabled &&
            canSeeDevCoaching && {
              path: AppRouter.DEV_COACHING.path,
              component: <DevCoaching />
            })
        },
        ...(!isTeamSpaceEnabled ? [activityMenuItem] : [])
      ]
    },
    {
      id: 'surveys',
      title: 'Surveys',
      subtitle:
        'Uncover qualitative data from your teams to improve developer experience and productivity',
      icon: (
        <span className='relative'>
          <LBSurveysIcon className='size-6' />
          <span className='bg-indigo-500 rounded-full size-5 absolute -top-2 -right-4 content-center text-white'>
            B
          </span>
        </span>
      ),
      topBarIcon: <LBSurveysIcon className='size-5 text-slate-500' />,
      badge: <BetaBadge />,
      permissions: {
        featureFlagName: FEATURE_FLAGS_NAMES.isSurveysEnabled,
        accountTypes: [ACCOUNT_TYPES_MAP.Primary, ACCOUNT_TYPES_MAP.SuperUser]
      },
      isExact: false,
      path: AppRouter.SURVEYS_LIST.path,
      showBadgeInTopBar: true,
      menu: [],
      component: <Surveys />
    },
    {
      id: 'automation',
      title: 'Automation',
      isBorderTop: true,
      icon: <AutomationIcon size={buttonIconSize} />,
      permissions: {
        featureFlagName: FEATURE_FLAGS_NAMES.automationsViewEnabled,
        gitProviders: [
          GIT_PROVIDERS.GITHUB,
          GIT_PROVIDERS.GITLAB,
          GIT_PROVIDERS.BITBUCKET
        ]
      },
      menu: [
        {
          type: 'subtitle',
          title:
            'Hit your goals, decrease toil, and streamline code to delivery with SEI workflow automations'
        },
        {
          id: 'automations_dashboard',
          title: 'Automations Dashboard',
          subtitle:
            'Visualize your existing workflow automations and their impact on your delivery pipeline',
          icon: (
            <AutomationsDashboardIcon size={menuIconSize} color={iconColor} />
          ),
          topBarIcon: (
            <AutomationsDashboardIcon
              size={topBarIconSize}
              color={topBarIconColor}
            />
          ),
          path: AppRouter.AUTOMATIONS.path
        },
        {
          id: 'marketplace',
          title: 'Marketplace',
          subtitle:
            'Discover new automations, recommendations, and tool plugins that will optimize your workflows',
          icon: <MarketplaceIcon size={menuIconSize} color={iconColor} />,
          topBarIcon: (
            <MarketplaceIcon size={topBarIconSize} color={topBarIconColor} />
          ),
          path: AppRouter.MARKETPLACE.path,
          permissions: {
            featureFlagName:
              FEATURE_FLAGS_NAMES.automationsMarketplaceViewEnable
          }
        }
      ]
    }
  ]);

  buttonsBuilder.initTabs([
    {
      id: 'settings',
      title: 'Settings',
      icon: <SettingsIcon size={buttonIconSize} />,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right'
      },
      transformOrigin: {
        vertical: 'bottom',
        horizontal: 'left'
      },
      // @ts-ignore
      permissions: {
        accountTypes: CAN_EDIT_ACCOUNT_TYPES
      },
      ...(isAllTeams && {
        path: canAccessAllCompanySettings
          ? AppRouter.GENERAL.path
          : AppRouter.TEAMS_SETTINGS.path,
        topBarIcon: (
          <SettingsIcon size={topBarIconSize} color={topBarIconColor} />
        ),
        isSelected: history.location.pathname.includes(AppRouter.SETTINGS.path)
      }),
      ...(!isAllTeams && {
        menu: [
          {
            title: (
              <TextWithTooltipOnEllipsis size='inherit' ellipsisAfter='22rem'>
                {selectedTeamName} Settings
              </TextWithTooltipOnEllipsis>
            ),
            icon: <TeamAvatar team={selectedTeam} size='small' />,
            topBarIcon: <TeamAvatar team={selectedTeam} size='small' />,
            path: isGroup
              ? AppRouter.PROJECT_MANAGEMENT.path
              : AppRouter.GENERAL.path,
            isSelected:
              history.location.pathname.includes(AppRouter.SETTINGS.path) &&
              !history.location.search.includes('isOrgSettings=true')
          },
          {
            title: 'Company Settings',
            ...(!rootTeamId &&
              !isTeamLicense && {
                onClick: () => {
                  const allTeamsGroup = getAllTeamsGroup(teams);
                  handleTeamChange({
                    team: allTeamsGroup,
                    selectTeamId: () =>
                      dispatch(selectTeamId(allTeamsGroup?.id)),
                    history,
                    isTeamLicense
                  });
                }
              }),
            path: canAccessAllCompanySettings
              ? AppRouter.GENERAL.path
              : isTeamLicense
              ? AppRouter.CONTRIBUTORS_SETTINGS.path
              : AppRouter.TEAMS_SETTINGS.path,
            search: 'isOrgSettings=true',
            icon: <Box width='2.2rem' height='2.2rem' />,
            topBarIcon: (
              <SettingsIcon size={topBarIconSize} color={topBarIconColor} />
            ),
            isSelected:
              history.location.pathname.includes(AppRouter.SETTINGS.path) &&
              history.location.search.includes('isOrgSettings=true')
          }
        ]
      })
    },
    {
      id: 'help',
      title: 'Help',
      icon: <HelpContainedIcon size={buttonIconSize} />,
      style: { marginTop: '0.8rem' },
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right'
      },
      transformOrigin: {
        vertical: 'bottom',
        horizontal: 'left'
      },
      menu: [
        {
          type: 'link',
          title: 'What is Software Engineering Intelligence+?',
          onClick: () => {
            window.open(
              'https://linearb.io/blog/software-engineering-intelligence-101'
            );
          }
        },
        {
          type: 'link',
          title: 'Help docs',
          onClick: () => {
            window.open('https://linearb.helpdocs.io/');
          }
        },
        {
          type: 'link',
          title: 'Blog',
          onClick: () => {
            window.open('https://linearb.io/blog');
          }
        },
        {
          type: 'link',
          title: 'Resources',
          onClick: () => {
            window.open('https://linearb.io/resources');
          }
        },
        {
          type: 'link',
          title: 'Dev Interrupted',
          onClick: () => {
            window.open('https://linearb.io/blog/');
          }
        },
        {
          type: 'link',
          title: 'gitStream',
          onClick: () => {
            window.open('https://docs.gitstream.cm/');
          }
        }
      ]
    }
  ]);

  return {
    navigationButtons: navigationButtonsBuilder.build(),
    buttons: buttonsBuilder.build()
  };
};

export default useSidebarButtons;
